/**
 * sindhi-lazy-modules.js
 * Sindhi Dry Fruits — load Horizon feature modules only where their element exists
 * ─────────────────────────────────────────────────────────────────────────────
 * snippets/scripts.liquid used to emit ~26 unconditional
 * <script type="module"> tags on EVERY page. Measured 2026-08-23 on live
 * /collections/gift-items: of the 28 Horizon feature modules loaded, only two
 * had their custom element in the served HTML (dialog-component x2,
 * quick-add-dialog x1). The other 26 -- slideshow, layered-slideshow,
 * media-gallery, product-sku, product-inventory, volume-pricing, and the rest
 * -- were fetched, parsed and executed to define elements the page never
 * renders. Roughly 214KB raw / ~40KB over the wire, and 26 requests.
 *
 * Why a tag-presence loader rather than Liquid template gating: quick-add
 * fetches a product page and injects its markup into #quick-add-modal-content
 * (see assets/quick-add.js + snippets/quick-add-modal.liquid). That injected
 * HTML contains product-form-component, variant-picker, product-price,
 * quantity-selector-component and more. Those modules ARE needed on a
 * collection page -- just not until the shopper opens quick-add. A
 * `{% if template == 'product' %}` gate would ship a broken quick-add.
 * Custom elements upgrade whenever their definition lands, so importing on
 * demand is exactly what the platform is built for.
 *
 * The tag -> URL map is emitted by snippets/scripts.liquid, so asset_url (and
 * its cache-busting ?v=) stays the single source of truth. Adding a module is
 * a one-line edit there; this file needs no changes.
 *
 * NOT handled here, deliberately:
 *   - dialog.js            registers a document-level 'toggle' listener for
 *                          [scroll-lock] on <details> (assets/dialog.js:178).
 *                          That has to run whether or not a dialog-component
 *                          exists, so it stays eagerly loaded.
 *   - popover-polyfill.js  defines no custom element -- nothing to key on.
 *   - view-transitions.js  no custom element, and render-blocking by design.
 *
 * Every module in the map guards its own registration with
 * `if (!customElements.get(...))`, so a double import is a no-op.
 */

(function () {
  'use strict';

  var mapNode = document.getElementById('sindhi-lazy-modules-map');
  if (!mapNode) return;

  var MAP;
  try {
    MAP = JSON.parse(mapNode.textContent);
  } catch (e) {
    /* There is no recovery here worth pretending to: the map is the only
       source of module URLs, so a map we cannot parse means we cannot load
       anything, and every lazy component on the page stays un-upgraded.
       Say so loudly instead of failing silently -- a malformed map can only
       come from a bad entry in scripts.liquid's lazy_modules list, which
       .claude/scripts/check-lazy-modules.py is there to catch before deploy. */
    console.error('[sindhi-lazy-modules] module map is not valid JSON; lazy components will not load.', e);
    return;
  }

  var TAGS = Object.keys(MAP);

  /* Resolved tags, and separately the URLs already injected. Both are needed:
     several modules register more than one element (quick-add.js owns both
     <quick-add-component> and <quick-add-dialog>), so a page can resolve two
     tags that point at one file. Keying only on the tag would inject that file
     twice. */
  var resolved = Object.create(null);
  var fetched = Object.create(null);

  function load(tag) {
    if (resolved[tag]) return;
    resolved[tag] = true;

    var url = MAP[tag];
    if (!url || fetched[url]) return;
    fetched[url] = true;

    var el = document.createElement('script');
    el.src = url;
    el.type = 'module';
    document.head.appendChild(el);
  }

  /* One pass over the parsed document. This script is itself deferred, so the
     whole body is available by the time it runs.

     Trade-off, stated plainly: the old tags began fetching during <head>
     parse, these begin after the body is parsed. Every one of them already
     carried fetchpriority="low", so none was on the critical path, and the
     modules a page does not use are no longer fetched at all. If a
     above-the-fold component ever measures as late to upgrade, the fix is a
     modulepreload for that specific tag, not a return to loading all 26. */
  function scan(root) {
    for (var i = 0; i < TAGS.length; i++) {
      var tag = TAGS[i];
      if (resolved[tag]) continue;
      if (root.querySelector(tag)) load(tag);
    }
  }

  scan(document);

  /* Anything inserted later: quick-add's injected product markup, cart drawer
     re-renders, Section Rendering API swaps, theme-editor block adds. */
  var pending = false;
  var observer = new MutationObserver(function () {
    /* Batch to one scan per frame -- a cart re-render fires many records. */
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      scan(document);
      if (allLoaded()) observer.disconnect();
    });
  });

  function allLoaded() {
    for (var i = 0; i < TAGS.length; i++) {
      if (!resolved[TAGS[i]]) return false;
    }
    return true;
  }

  if (!allLoaded()) {
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
